import { Middleware } from "@reduxjs/toolkit";
import { RootState } from "..";
import {
  addWeaponMount,
  removeWeaponMount,
  updateWeaponMount,
  setPowerPlant,
  setFuelTanks,
} from "../features/shipBuilderSlice";
import { calculatePowerPlantRequirements } from "../../utils/calculations/powerCalculations";
import { calculateFuelTankRequirements } from "../../utils/calculations/fuelCalculations";

const weaponPowerPlantMiddleware: Middleware<{}, RootState> =
  (store) => (next) => (action: any) => {
    // First, let the action pass through and update the state
    const result = next(action);

    // Check if this is a weapon mount action that affects power requirements
    if (
      addWeaponMount.match(action) ||
      removeWeaponMount.match(action) ||
      updateWeaponMount.match(action)
    ) {
      // Get the updated state after the action has been processed
      const state = store.getState().shipBuilder;
      const { weaponMounts, shipSize, techLevel } = state;

      // Skip calculation if state is not properly initialized
      if (!shipSize || shipSize <= 0) {
        console.log("weaponPowerPlantMiddleware: Skipping calculation - ship size not initialized");
        return result;
      }

      // Calculate total power requirements from weapon mounts
      const weaponPowerRequirement = weaponMounts.reduce((total, mount) => {
        const mountQuantity = mount.quantity || 1;
        const mountPower = mount.weapons.reduce((weaponTotal, weapon) => {
          return weaponTotal + (weapon.power || 0);
        }, 0);
        return total + (mountPower * mountQuantity);
      }, 0);

      console.log("weaponPowerPlantMiddleware: Weapon power requirement:", weaponPowerRequirement);

      // Get current power plant
      const currentPowerPlant = state.powerPlant;
      const currentPowerOutput = currentPowerPlant ? currentPowerPlant.power : 0;

      // Calculate total power required by the ship (including drives, bridge, sensors, etc.)
      // Use the ship stats if available, otherwise calculate basic requirements
      const totalPowerRequired = state.shipStats?.powerRequired || (shipSize * 0.2); // Basic systems power as fallback

      // Check if current power plant can handle the new requirements
      if (currentPowerOutput < totalPowerRequired) {
        console.log(`weaponPowerPlantMiddleware: Power insufficient (${currentPowerOutput} < ${totalPowerRequired}), updating power plant`);
        
        // Calculate required power plant
        const powerPlantResult = calculatePowerPlantRequirements({
          powerRequired: totalPowerRequired,
          shipSize,
          techLevel,
        });

        if (powerPlantResult.success && powerPlantResult.data) {
          // Update power plant
          store.dispatch(setPowerPlant(powerPlantResult.data));

          // Update fuel tanks based on new power plant
          const fuelTankResult = calculateFuelTankRequirements({
            powerPlant: powerPlantResult.data,
            drives: state.drives,
            shipSize,
          });

          if (fuelTankResult.success && fuelTankResult.data) {
            store.dispatch(setFuelTanks(fuelTankResult.data.total));
          }

          console.log("weaponPowerPlantMiddleware: Power plant and fuel tanks updated");
        }
      } else {
        console.log(`weaponPowerPlantMiddleware: Power sufficient (${currentPowerOutput} >= ${totalPowerRequired}), no update needed`);
      }
    }

    return result;
  };

export default weaponPowerPlantMiddleware;
